import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useParams, useLocation } from 'react-router-dom';
import { LayoutDashboard, Brain, MessageCircle, Settings, FlaskConical, Bot, Loader2, BookOpen, Package, ChevronDown, Layers } from 'lucide-react';
import { workerApi, workerApiService } from '../../services/workerApi';
import { apiService } from '../../services/api';
import { mapApiPlatform } from '../../types/platform';
import type { Platform } from '../../types/platform';
import { ChatbotContext } from '../../context/ChatbotContext';

const PLATFORM_PILL_CLS: Record<string, string> = {
  facebook: 'bg-[#1877F2]/10 text-[#1877F2]',
  zalo:     'bg-[#0068FF]/10 text-[#0068FF]',
  instagram:'bg-[#E1306C]/10 text-[#E1306C]',
};

// Simple flat links (label = i18n key)
const SIMPLE_TABS = [
  { labelKey: 'tabOverview',       icon: <LayoutDashboard size={15} />, to: '',         end: true  },
  { labelKey: 'tabConversations',  icon: <MessageCircle size={15} />,   to: 'messages', end: false },
  { labelKey: 'tabTest',           icon: <FlaskConical size={15} />,    to: 'test',     end: false },
  { labelKey: 'tabSettings',       icon: <Settings size={15} />,        to: 'settings', end: false },
];

// Dropdown group items (label = i18n key)
const CONTENT_GROUP = [
  { labelKey: 'menuAiConfig',    icon: <Brain size={14} />,    to: 'config'        },
  { labelKey: 'menuProducts',    icon: <Package size={14} />,  to: 'products'      },
  { labelKey: 'menuKnowledge',   icon: <BookOpen size={14} />, to: 'knowledge-base' },
];
const CONTENT_GROUP_PATHS = CONTENT_GROUP.map((c) => c.to);

const tabLinkCls = ({ isActive }: { isActive: boolean }) =>
  `flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
    isActive
      ? 'text-gray-900 border-[#3390ec]'
      : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50'
  }`;

/** Dropdown tab for the "Nội dung" group */
const ContentDropdown: React.FC<{ basePath: string }> = ({ basePath }) => {
  const { t } = useTranslation('chatbot');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const isGroupActive = CONTENT_GROUP_PATHS.some((p) =>
    location.pathname.includes(`/${p}`)
  );

  // Close when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close when navigating away
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
          isGroupActive
            ? 'text-gray-900 border-[#3390ec]'
            : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50'
        }`}
      >
        <Layers size={15} />
        {t('menuContent')}
        <ChevronDown
          size={13}
          className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-0.5 w-52 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 z-50">
          {CONTENT_GROUP.map((item) => (
            <NavLink
              key={item.to}
              to={`${basePath}/${item.to}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${
                  isActive
                    ? 'text-[#3390ec] bg-[#3390ec]/5 font-medium'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`
              }
            >
              <span className="text-gray-400">{item.icon}</span>
              {t(item.labelKey)}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

// Refresh the worker URL + JWT 5 minutes before the 1-hour JWT expires.
// This ensures direct-mode calls never see a 401 from an expired token,
// and also picks up any new tunnel URL if cloudflared has restarted.
const WORKER_URL_REFRESH_INTERVAL_MS = 55 * 60 * 1000; // 55 minutes

interface ChatbotDetailProps {
  /** Platforms already loaded by App.tsx — used for instant display without an extra API call. */
  platforms?: Platform[];
}

/**
 * Layout shell for /chatbot/:id/*
 * - Uses platforms prop (pre-loaded by App.tsx) for instant display
 * - After worker direct mode is set, refreshes account data from worker
 * - Provides account + setAccount + refreshAccount via ChatbotContext
 * - Renders the top bar (avatar + info + tab NavLinks)
 * - <Outlet /> renders the active child page
 */
export const ChatbotDetail: React.FC<ChatbotDetailProps> = ({ platforms }) => {
  const { t } = useTranslation('chatbot');
  const { id } = useParams<{ id: string }>();
  const [account, setAccount] = useState<Platform | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [workerUrl, setWorkerUrl] = useState('');
  const [jwtToken, setJwtToken] = useState('');
  // Track whether we already fetched the worker URL for this chatbot id
  const workerUrlFetchedFor = useRef<string | null>(null);
  // Periodic refresh timer — clears on unmount
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Use pre-loaded platforms prop for instant display (no API call needed on mount)
  useEffect(() => {
    if (!id) return;
    const cached = platforms?.find((p) => p.id === id);
    if (cached) {
      setAccount(cached);
      setLoading(false);
      setFetchError(false);
    } else {
      // Not found in prop list yet — show loading until direct mode refresh
      setLoading(true);
    }
  }, [id, platforms]);

  // Refresh account from worker after direct mode is ready (uses getPlatforms list)
  const fetchAccount = useCallback(async () => {
    if (!id) return;
    try {
      const res = await workerApi.getPlatforms();
      const found = (res.data || []).find((p: any) => p.id === id);
      if (found) {
        setAccount(mapApiPlatform(found));
        setFetchError(false);
      } else {
        setAccount(prev => {
          if (!prev) setFetchError(true);
          return prev;
        });
      }
    } catch (e) {
      console.error('[ChatbotDetail] Failed to refresh platform from worker:', e);
      setAccount(prev => {
        if (!prev) setFetchError(true);
        return prev;
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch (and periodically refresh) the worker tunnel URL + JWT.
  //
  useEffect(() => {
    if (!id) return;
    
    // Clean up direct mode (legacy)
    workerApiService.clearDirectMode();
    fetchAccount();

    return () => {
      workerApiService.clearDirectMode();
    };
  }, [id, fetchAccount]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3">
        <Loader2 size={32} strokeWidth={1.5} className="animate-spin text-gray-300" />
        <p className="text-sm text-gray-400">{t('loading')}</p>
      </div>
    );
  }

  if (fetchError || !account) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-300 gap-3">
        <Bot size={48} strokeWidth={1} />
        <p className="text-sm">{t('notFound')}</p>
      </div>
    );
  }

  return (
    <ChatbotContext.Provider
      value={{
        account,
        setAccount: setAccount as React.Dispatch<React.SetStateAction<Platform>>,
        refreshAccount: fetchAccount,
        workerUrl,
        jwtToken,
      }}
    >
      <div className="flex flex-col h-full overflow-hidden">
        {/* ── Top bar ──────────────────────────────────────────────────── */}
        <div className="bg-white border-b border-gray-100 flex-shrink-0">
          {/* Identity row */}
          <div className="flex items-center gap-3 px-6 pt-5 pb-4">
            <div className="relative">
              <img
                src={account.avatar}
                alt={account.name}
                className="w-11 h-11 rounded-xl object-cover bg-gray-100"
              />
              <span
                className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                  account.status === 'active'
                    ? 'bg-emerald-400'
                    : account.status === 'error'
                    ? 'bg-red-400'
                    : 'bg-gray-300'
                }`}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-base font-bold text-gray-900 leading-tight truncate">
                {account.name}
              </h1>
              <p className="text-xs text-gray-400 font-mono truncate">{account.platform_id}</p>
            </div>
            <span
              className={`px-3 py-1 rounded-full text-xs font-semibold capitalize shrink-0 ${
                PLATFORM_PILL_CLS[account.platform] ?? 'bg-[#3390ec]/10 text-[#3390ec]'
              }`}
            >
              {account.platform}
            </span>
          </div>

          {/* Tab bar */}
          <div className="flex px-6 border-t border-gray-50 items-stretch relative">
            {/* Tổng quan */}
            <NavLink to={SIMPLE_TABS[0].to} end={SIMPLE_TABS[0].end} className={tabLinkCls}>
              {SIMPLE_TABS[0].icon}
              {t(SIMPLE_TABS[0].labelKey)}
            </NavLink>

            {/* Nội dung dropdown (Cấu hình AI / Sản phẩm / KB) */}
            <ContentDropdown basePath={`/chatbot/${id}`} />

            {/* Remaining simple tabs */}
            {SIMPLE_TABS.slice(1).map((tab) => (
              <NavLink key={tab.to} to={tab.to} end={tab.end} className={tabLinkCls}>
                {tab.icon}
                {t(tab.labelKey)}
              </NavLink>
            ))}
          </div>
        </div>

        {/* ── Page content via Outlet ───────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </div>
      </div>
    </ChatbotContext.Provider>
  );
};
